import { Form } from '@/components/ui/form/form';
import { useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { CurrencyField } from '../ui/field/currency-field';
import { ExternalAccountField } from '../ui/field/external-account-field';
import { TextAreaField } from '../ui/field/text-area-field';
import { TextField } from '../ui/field/text-field';
import {
  Account,
  getExternalAccountOptions,
  useAccountForm,
  useDateFormatter,
  useExternalAccounts,
  useTimeFormatter,
} from '@liquidbudget/common';
import { AccountTypeSelectField } from '@/components/account-form/account-type-select';

type Props = {
  account?: Account;
  onSuccess: () => void;
};

export function AccountAddForm({ account, onSuccess }: Props) {
  const externalAccounts = useExternalAccounts();
  const { form, onSubmit, isSubmitting, offBudgetGroupPlaceholder } = useAccountForm({ account, onSuccess });
  const formatDate = useDateFormatter();
  const formatTime = useTimeFormatter();

  const selectedId = form.watch('externalAccountId');
  const isArchived = account?.state === 'ARCHIVED';

  // map external accounts into select options
  const externalAccountGroups = useMemo(() => {
    return getExternalAccountOptions(externalAccounts);
  }, [externalAccounts]);

  // if an external account is chosen, automatically set the form's balance
  useEffect(() => {
    if (selectedId && externalAccounts) {
      const linked = externalAccounts.find((acc) => acc.id === selectedId);
      if (linked?.balance) {
        form.setValue('balance', linked.balance);
      }
    }
  }, [selectedId, externalAccounts, form]);

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-8 mb-2">
        <div className="grid gap-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8">
              <TextField form={form} name="name" label="Account Name" disabled={isArchived} required />
            </div>
            <div className="col-span-4">
              <AccountTypeSelectField form={form} name="type" label="Type" disabled={isArchived} />
            </div>
          </div>

          <TextField
            form={form}
            name="offBudgetGroup"
            label="Account Group"
            placeholder={offBudgetGroupPlaceholder}
            disabled={isArchived}
          />

          <ExternalAccountField
            control={form.control}
            name="externalAccountId"
            groups={externalAccountGroups}
            disabled={isArchived || externalAccountGroups.length === 0}
          />
          {account?.externalAccountId != null && account?.externalAccountLastSyncTime != null && (
            <p className="text-xs text-muted-foreground -mt-3 text-center">
              Imports are attempted every hour. The latest response was at{' '}
              {formatTime(account.externalAccountLastSyncTime)} on {formatDate(account.externalAccountLastSyncTime)}
            </p>
          )}

          {!account && (
            <CurrencyField
              control={form.control}
              allowNegativeValue={true}
              name="balance"
              label="Current Balance"
              disabled={isArchived}
              showAdjustment={true}
              description="This is your current account balance"
            />
          )}

          <TextAreaField
            form={form}
            name="notes"
            label="Notes"
            placeholder="Optional information about your account.."
            disabled={isArchived}
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button type="submit" isLoading={isSubmitting}>
            {account ? 'Update Account' : 'Add Account'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
